/** Driver profile (eskiz 9): sarlavha (driverning mahalliy vaqti bilan), keyingi qadam (eslatma), status yo'li, tablar,
 * ma'lumotlar, arizalar, faollik. */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { recordVisit } from "@/features/widgets/recent/store";
import { AlertTriangle, ArrowRight, Check, Copy, Eye, EyeOff, FileText, Mail, MessageSquare, MessageSquareText, MoreHorizontal, Pencil, Phone, Plus } from "lucide-react";
import { ApiError } from "@/lib/api";
import { cn, dayjs, formatDate, formatDateTime, formatWhen } from "@/lib/utils";
import { useMe } from "@/features/auth/hooks";
import { can, isManager } from "@/features/auth/types";
import {
  driverKeys,
  useAppSettings,
  useChangeStatus,
  useDriver,
  useDriverActivities,
  useDriverHistory,
  useDriverReminders,
  useLogActivity,
  useMarkRetention,
  useReminderAction,
  useUpdateDriver,
  useUserOptions,
} from "@/features/drivers/api";
import {
  CALL_OUTCOMES,
  LOST_STATUSES,
  PIPELINE_ORDER,
  SOURCE_LABEL,
  type Activity,
  type ApplicationBrief,
  type DriverDetail,
  type DriverStatus,
  type Reminder,
} from "@/features/drivers/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { StatusPill } from "@/components/ui/badge";
import { NumberInput, Select, Textarea } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { Tabs } from "@/components/ui/tabs";
import { Avatar, EmptyState, ErrorState, PageLoading } from "@/components/ui/misc";
import { Table, Td, Th, Tr } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { ChangeStatusModal } from "./ChangeStatusModal";
import { AddReminderModal } from "./AddReminderModal";
import { driverTz, tzAbbr } from "@/features/drivers/timezones";
import { compactMenu, ContextMenu } from "@/components/ui/context-menu";
import { copyText, openInNewTab } from "@/lib/clipboard";
import { driverApplicationMenu } from "@/features/drivers/menu";
import { useDriverActions } from "./useDriverActions";
import { StageBar } from "./StageBar";
import { DocumentsCard, DocumentsTab } from "./DocumentsTab";
import { allowedTargets, NEEDS_JOB } from "@/features/drivers/transitions";
import { DriverFormFields, fromDriverDetail, toDriverWrite, useDriverSchema, type DriverFormValues } from "./NewDriverPage";
import { PersonLink } from "@/components/PersonLink";
import { canReceive, FeeBadge, FeeEditModal, PaymentModal, type FeeTarget } from "@/pages/budget/FeeParts";
import { DriverLocalClock } from "@/features/drivers/DriverLocalTime";
import { rememberDriver } from "@/features/drivers/recent";

/** "Oct 7, 9:00 CST (18:00 Tashkent)" */
function formatDual(iso: string, dTz: string, myTz: string): string {
  const d = dayjs(iso);
  try {
    const local = d.tz(dTz);
    const mine = d.tz(myTz);
    const city = myTz.split("/").pop()?.replace(/_/g, " ") ?? myTz;
    return `${local.format("MMM D, H:mm")} ${tzAbbr(dTz, d.toDate())} (${mine.format("H:mm")} ${city})`;
  } catch {
    return d.format("MMM D, H:mm");
  }
}

const daysUntil = (date: string) => dayjs(date).startOf("day").diff(dayjs().startOf("day"), "day");

const TABS = ["overview", "applications", "activity", "documents", "history"];

// ---- Sahifa ----

export function DriverProfilePage() {
  const { t } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  const { id } = useParams();
  const me = useMe().data;
  const driver = useDriver(id);
  // Oxirgi ochilganlar widgeti (shu qurilmada)
  const visitId = driver.data?.id;
  const visitTitle = driver.data?.full_name;
  const visitState = driver.data?.state;
  useEffect(() => {
    if (visitId !== undefined && visitTitle) recordVisit({ kind: "driver", id: visitId, title: visitTitle, subtitle: visitState ?? "" });
  }, [visitId, visitTitle, visitState]);
  // ?tab=documents -- o'ng tugma menyusidagi "Hujjatlar" va boshqa havolalar kerakli tabni ochadi
  const [params] = useSearchParams();
  const urlTab = params.get("tab");
  const [tab, setTab] = useState(() => (urlTab && TABS.includes(urlTab) ? urlTab : "overview"));
  useEffect(() => {
    if (urlTab && TABS.includes(urlTab)) setTab(urlTab);
  }, [urlTab]);
  const [statusOpen, setStatusOpen] = useState(false);
  const [initialStatus, setInitialStatus] = useState<DriverStatus | undefined>();
  const [editOpen, setEditOpen] = useState(false);
  const [composerType, setComposerType] = useState<"note" | "call" | "sms" | "email">("note");
  const [focusKey, setFocusKey] = useState(0);
  const driverActions = useDriverActions({ mode: "profile" });
  const settings = useAppSettings();
  const qc = useQueryClient();
  const change = useChangeStatus(id ?? "");
  const [pending, setPending] = useState<DriverStatus | null>(null);
  const staleDays = Number(settings.data?.pipeline_stale_days ?? 7);
  // ⌘K dagi "oxirgi driverlar" ro'yxati uchun
  const loaded = driver.data;
  useEffect(() => {
    if (loaded) rememberDriver(loaded, me?.id);
  }, [loaded, me?.id]);

  if (driver.isPending) return <PageLoading />;
  if (driver.isError) return <ErrorState message={driver.error.message} onRetry={() => driver.refetch()} />;
  if (!me) return null;
  const d = driver.data;
  const myTz = me.timezone || "Asia/Tashkent";
  const dTz = driverTz(d.state);

  function openStatus(preset?: DriverStatus) {
    setInitialStatus(preset);
    setStatusOpen(true);
  }

  function logCall() {
    setTab("overview");
    setComposerType("call");
    setFocusKey((k) => k + 1);
  }

  function addNote() {
    setTab("overview");
    setComposerType("note");
    setFocusKey((k) => k + 1);
  }

  // Bosqich o'zgarishi -- bar, "Keyingi" tugmasi va menyu uchun bitta yo'l (pipeline doskasi bilan bir xil):
  // ish yoki sabab kerak bo'lsa oyna, aks holda optimistik so'rov; rad etilsa yoki 409 bo'lsa orqaga qaytadi
  const allowed = allowedTargets(d.status, isManager(me.role));
  function moveTo(to: DriverStatus) {
    if (to === d.status || pending) return;
    if (to === "hired" || LOST_STATUSES.includes(to) || (NEEDS_JOB.includes(to) && !d.current_job)) {
      openStatus(to);
      return;
    }
    setPending(to);
    change.mutate(
      { status: to, version: d.version },
      {
        onSuccess: () => {
          setPending(null);
          toast.success(t("statusModal.updated"), `${d.full_name} → ${tc(`status.${to}`)}`);
        },
        onError: (err) => {
          setPending(null);
          if (err instanceof ApiError && err.isConflict) {
            toast.warning(t("statusModal.changedBy", { who: err.changedBy ?? t("statusModal.someoneElse") }), t("statusModal.reloadedHint"));
            qc.invalidateQueries({ queryKey: driverKeys.detail(d.id) });
            qc.invalidateQueries({ queryKey: driverKeys.all });
            return;
          }
          toast.error(err instanceof ApiError ? err.message : t("statusModal.failed"));
        },
      },
    );
  }

  // Keyingi bosqich (yo'nalish bo'yicha): "Keyingi: ..." tugmasi; boshqa o'tishlar barda va "..." menyusida
  const lost = LOST_STATUSES.includes(d.status);
  const stageIdx = lost ? -1 : PIPELINE_ORDER.indexOf(d.status);
  const nextStatus = stageIdx >= 0 && stageIdx < PIPELINE_ORDER.length - 1 ? PIPELINE_ORDER[stageIdx + 1] : null;
  const stale = d.days_in_stage >= staleDays;
  const sourceLabel = SOURCE_LABEL[d.source] ? tc(`source.${d.source}`) : d.source;
  const expLabel = d.experience_label ? d.experience_label.replace(/^(\d+)\s*yrs?$/, (_m, n) => t("profile.details.year", { count: Number(n) })) : "";
  const metaLine = [d.cdl_class ? t("profile.details.classN", { cls: d.cdl_class }) : "", d.driver_type ? tc(`driverType.${d.driver_type}`) : "", expLabel, [d.city, d.state].filter(Boolean).join(", ")]
    .filter(Boolean)
    .join(" · ");

  const tabs = [
    { key: "overview", label: t("profile.tabs.overview") },
    { key: "applications", label: t("profile.tabs.applications", { n: d.applications.length }) },
    { key: "activity", label: t("profile.tabs.activity", { n: d.activity_count }) },
    { key: "documents", label: t("profile.tabs.documents", { n: d.documents_summary.received, total: d.documents_summary.total }) },
    { key: "history", label: t("profile.tabs.history") },
  ];

  return (
    <div>
      <div className="mb-2 text-[12px] text-muted">
        <Link to="/drivers" className="hover:underline">
          {t("list.title")}
        </Link>{" "}
        / <span className="font-semibold text-ink">{d.full_name}</span>
      </div>

      <ContextMenu items={() => driverActions.items(d)}>
        <Card className="px-7 py-6">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-5">
            <div className="flex min-w-0 flex-[2_1_420px] items-center gap-5">
              <Avatar initials={`${d.first_name[0] ?? ""}${d.last_name[0] ?? ""}`.toUpperCase()} name={d.full_name} size={76} className="text-[26px] font-medium tracking-[0.02em]" />
              <div className="min-w-0 space-y-2">
                <h1 className="text-[34px] leading-[1.05] font-semibold tracking-[-0.03em]">{d.full_name}</h1>
                {metaLine && <div className="text-[14px] text-muted">{metaLine}</div>}
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill status={d.status} />
                  {/* Bosqichda necha kun: pipeline kartasidagidek, uzoq tursa qizil */}
                  {!lost && d.status !== "hired" && (
                    <span className={cn("rounded-full px-3 py-1 text-[12.5px] font-semibold", stale ? "bg-danger-soft text-danger" : "bg-fill text-ink-soft")}>{t("profile.daysInStage", { count: d.days_in_stage })}</span>
                  )}
                  <span className="rounded-full bg-fill px-3 py-1 text-[12.5px] font-medium text-muted">{sourceLabel}</span>
                  {d.legal_status && <span className="rounded-full bg-fill px-3 py-1 text-[12.5px] font-medium text-muted">{tc(`legalStatus.${d.legal_status}`)}</span>}
                  {d.freight_type && <span className="rounded-full bg-fill px-3 py-1 text-[12.5px] font-medium text-muted">{tc(`freight.${d.freight_type}`)}</span>}
                  {d.status === "hired" && (
                    <span className={cn("text-[12px] font-semibold", d.hire_approved_at ? "text-success" : "text-warning")}>
                      {d.hire_approved_at ? t("profile.approvedBy", { name: d.hire_approved_by?.display_name ?? t("profile.manager") }) : t("profile.pendingApproval")}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-[1_1_300px] flex-col gap-4">
              {d.state && <DriverLocalClock state={d.state} />}
              <div className="flex flex-wrap items-center gap-2">
                {/* Sahifadagi yagona sariq tugma -- qo'ng'iroq (recruiter'ning asosiy ishi) */}
                <Button variant="primary" size="lg" onClick={logCall} disabled={!d.phone} title={!d.phone ? t("form.noPhoneWarning") : undefined}>
                  <Phone size={16} strokeWidth={2} /> {t("profile.hero.call")}
                </Button>
                <Button size="lg" onClick={addNote}>
                  <MessageSquare size={16} /> {t("profile.hero.note")}
                </Button>
                {nextStatus && (
                  <Button size="lg" onClick={() => moveTo(nextStatus)} loading={pending === nextStatus}>
                    {t("profile.hero.moveTo", { stage: tc(`status.${nextStatus}`) })} <ArrowRight size={16} />
                  </Button>
                )}
                <button
                  type="button"
                  aria-label={t("profile.hero.more")}
                  title={t("profile.hero.more")}
                  onClick={(e) => {
                    // Shu kartaning o'ng tugma menyusini chap tugma bilan ochadi (bir xil amallar, bir joyda)
                    const r = e.currentTarget.getBoundingClientRect();
                    e.currentTarget.dispatchEvent(new MouseEvent("contextmenu", { bubbles: true, cancelable: true, clientX: r.left, clientY: r.bottom + 6 }));
                  }}
                  className="grid h-11 w-11 place-items-center rounded-full bg-fill text-ink-soft transition-colors hover:bg-fill-hover hover:text-ink"
                >
                  <MoreHorizontal size={18} aria-hidden />
                </button>
              </div>
              {!d.phone && (
                <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-warning">
                  <AlertTriangle size={13} /> {t("profile.noPhone")} · {t("form.noPhoneWarning")}
                </span>
              )}
            </div>
          </div>
        </Card>
      </ContextMenu>

      <StageBar status={d.status} reason={d.status_reason} days={d.days_in_stage} staleDays={staleDays} allowed={allowed} pending={pending} onPick={moveTo} />

      <Tabs items={tabs} value={tab} onChange={setTab} className="mb-4" />

      {tab === "overview" && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.15fr_1fr]">
          <div className="space-y-4">
            <DetailsCard d={d} onEdit={() => setEditOpen(true)} />
            <ActivityPanel d={d} myTz={myTz} composerType={composerType} setComposerType={setComposerType} focusKey={focusKey} onShowAll={() => setTab("activity")} />
          </div>
          <div className="space-y-4">
            <NextStepCard d={d} dTz={dTz} myTz={myTz} />
            <DocumentsCard d={d} onOpen={() => setTab("documents")} />
            {d.status === "hired" && <RetentionCard d={d} />}
            <Card>
              <CardHeader
                title={t("profile.applications", { n: d.applications.length })}
                action={
                  <Button size="sm" onClick={() => openStatus("submitted")} disabled={d.status === "hired" || lost}>
                    <Plus size={12} /> {t("profile.submitToJob")}
                  </Button>
                }
              />
              <CardBody>
                <ApplicationsTable apps={d.applications} driverName={d.full_name} compact />
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {tab === "applications" && (
        <Card>
          <CardBody className="pt-4">
            <ApplicationsTable apps={d.applications} driverName={d.full_name} />
          </CardBody>
        </Card>
      )}

      {tab === "activity" && <ActivityFeedFull driverId={d.id} myTz={myTz} />}

      {tab === "documents" && <DocumentsTab d={d} />}

      {tab === "history" && <HistoryList driverId={d.id} myTz={myTz} />}

      {driverActions.dialogs}
      <ChangeStatusModal driver={d} documentsSummary={d.documents_summary} open={statusOpen} onOpenChange={setStatusOpen} initialStatus={initialStatus} />
      <EditDriverModal d={d} open={editOpen} onOpenChange={setEditOpen} canEditSensitive={d.can_edit_sensitive} showOwner={can(me, "drivers_transfer")} />
    </div>
  );
}

// ---- Keyingi qadam: eng yaqin eslatma (o'ng ustunning tepasi) ----

function NextStepCard({ d, dTz, myTz }: { d: DriverDetail; dTz: string; myTz: string }) {
  const { t } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  const reminders = useDriverReminders(d.id);
  const act = useReminderAction();
  const [reminderOpen, setReminderOpen] = useState(false);

  const next = useMemo(() => {
    const pending = (reminders.data ?? []).filter((r) => !r.is_done);
    return pending.sort((a, b) => a.effective_due.localeCompare(b.effective_due))[0] ?? null;
  }, [reminders.data]);

  if (reminders.isPending) return <div className="skeleton h-[132px] rounded-card" aria-hidden />;
  return (
    <Card className={cn("p-5", next?.overdue && "border-warning/40")}>
      <div className="text-[11px] font-semibold tracking-[0.06em] text-muted uppercase">
        {t("profile.nextStep.title")}
        {next?.overdue && <span className="text-warning"> · {t("profile.nextStep.overdue")}</span>}
      </div>
      {next ? (
        <>
          <div className="mt-2 text-[17px] leading-snug font-semibold">
            {tc(`reminder.${next.kind}`)}
            {next.note ? ` · ${next.note}` : ""}
          </div>
          <div className="mt-1 text-[13px] text-muted">{formatDual(next.effective_due, dTz, myTz)}</div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="ink" loading={act.isPending} onClick={() => act.mutate({ id: next.id, action: "done" }, { onSuccess: () => toast.success(t("profile.activity.reminderDone")) })}>
              {t("profile.nextStep.markDone")}
            </Button>
            <Button size="sm" onClick={() => act.mutate({ id: next.id, action: "snooze", snooze_until: dayjs().add(1, "day").hour(9).minute(0).second(0).toISOString() })}>
              {t("profile.activity.tomorrow")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setReminderOpen(true)}>
              <Plus size={12} /> {t("profile.activity.addReminder")}
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="mt-2 text-[13px] text-muted">{t("profile.nextStep.none")}</p>
          <Button size="sm" className="mt-3" onClick={() => setReminderOpen(true)}>
            <Plus size={12} /> {t("profile.activity.addReminder")}
          </Button>
        </>
      )}
      <AddReminderModal driverId={d.id} dTz={dTz} open={reminderOpen} onOpenChange={setReminderOpen} defaultAssignee={d.owner?.id ?? null} />
    </Card>
  );
}

// ---- Ma'lumotlar ----

function DetailsCard({ d, onEdit }: { d: DriverDetail; onEdit: () => void }) {
  const { t } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  const [reveal, setReveal] = useState(false);
  const medical = d.medical_card_expires ? daysUntil(d.medical_card_expires) : null;
  const cdl = d.cdl_expires ? daysUntil(d.cdl_expires) : null;
  const expiryNote = (days: number | null) => (days === null ? "" : days < 0 ? ` · ${t("profile.details.expired", { days: -days })}` : days < 60 ? ` · ${t("profile.details.expiresIn", { days })}` : "");
  const prefs = [d.home_time ? t("profile.details.home", { value: d.home_time.toLowerCase() }) : "", d.desired_pay].filter(Boolean).join(" · ");
  // "3 yrs" / "1 yr" -> tarjima; boshqa ko'rinish o'zgarmay qoladi
  const expLabel = d.experience_label ? d.experience_label.replace(/^(\d+)\s*yrs?$/, (_m, n) => t("profile.details.year", { count: Number(n) })) : "";
  const exp = [expLabel, d.driving_type].filter(Boolean).join(" ");

  // Faktlar plitkalari (oldingi reja): yorliq + qiymat, muhim muddatlar sariq, maxfiylar yashirin
  type Fact = { key: string; label: string; value: ReactNode; tone?: "warning" | "muted"; wide?: boolean };
  const facts: Fact[] = [
    { key: "phone", label: t("profile.details.phone"), value: d.phone ? [d.phone_display, d.phone_secondary_display].filter(Boolean).join(" · ") : "—" },
    { key: "email", label: t("profile.details.email"), value: d.email || "—" },
    {
      key: "cdl",
      label: t("profile.details.cdl"),
      value:
        d.cdl_class || d.cdl_number_masked ? (
          <span className="inline-flex items-center gap-2">
            {[d.cdl_class ? t("profile.details.classN", { cls: d.cdl_class }) : "", d.cdl_state, reveal && d.cdl_number ? d.cdl_number : d.cdl_number_masked].filter(Boolean).join(" · ")}
            {d.can_edit_sensitive && d.cdl_number && (
              <button type="button" onClick={() => setReveal((r) => !r)} className="text-muted hover:text-ink" aria-label={reveal ? t("profile.details.hideCdl") : t("profile.details.showCdl")}>
                {reveal ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            )}
          </span>
        ) : (
          "—"
        ),
    },
    { key: "cdlExpires", label: t("profile.details.cdlExpires"), value: `${formatDate(d.cdl_expires)}${expiryNote(cdl)}`, tone: cdl !== null && cdl < 60 ? "warning" : undefined },
    { key: "medical", label: t("profile.details.medicalExpires"), value: `${formatDate(d.medical_card_expires)}${expiryNote(medical)}`, tone: medical !== null && medical < 60 ? "warning" : undefined },
    { key: "experience", label: t("profile.details.experience"), value: exp || "—" },
    { key: "endorsements", label: t("profile.details.endorsements"), value: d.endorsements.length ? d.endorsements.join(", ") : "—" },
    { key: "equipment", label: t("profile.details.equipment"), value: d.equipment.length ? d.equipment.join(", ") : "—" },
    { key: "preferences", label: t("profile.details.preferences"), value: prefs || "—" },
    { key: "available", label: t("profile.details.availableFrom"), value: formatDate(d.available_from) },
    {
      key: "owner",
      label: t("profile.details.owner"),
      value: (
        <>
          {d.owner ? <PersonLink id={d.owner.id} name={d.owner.display_name} /> : t("profile.pool")}
          {d.assigned_until && <span className="text-muted"> · {t("profile.assignedUntil", { date: formatDate(d.assigned_until) })}</span>}
        </>
      ),
    },
    { key: "added", label: t("profile.details.added"), value: formatDate(d.created_at) },
    { key: "dob", label: t("profile.details.dob"), value: d.date_of_birth_masked || "—", tone: "muted" },
  ];
  if (d.legal_status) facts.push({ key: "legalStatus", label: t("profile.details.legalStatus"), value: tc(`legalStatus.${d.legal_status}`) });
  if (d.source === "referral" && d.referred_by) facts.push({ key: "referredBy", label: t("profile.details.referredBy"), value: d.referred_by });
  if (d.import_ref) facts.push({ key: "import", label: t("profile.details.importedFrom"), value: d.import_ref, tone: "muted" });
  if (d.notes) facts.push({ key: "notes", label: t("profile.details.notes"), value: <span className="whitespace-pre-line">{d.notes}</span>, wide: true });

  return (
    <Card>
      <CardHeader
        title={t("profile.details.title")}
        action={
          <Button size="sm" onClick={onEdit}>
            <Pencil size={12} /> {tc("actions.edit")}
          </Button>
        }
      />
      <CardBody>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(170px,100%),1fr))] gap-2.5">
          {facts.map((f) => (
            <div key={f.key} className={cn("min-w-0 rounded-2xl bg-fill/50 px-4 py-3", f.wide && "col-span-full")}>
              <div className="text-[11px] font-semibold tracking-[0.06em] text-muted uppercase">{f.label}</div>
              <div className={cn("mt-1 text-[14px] font-medium break-words", f.tone === "warning" && "text-warning", f.tone === "muted" && "text-muted")}>{f.value}</div>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

function RetentionCard({ d }: { d: DriverDetail }) {
  const { t } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  const mark = useMarkRetention(d.id);
  const rows: { days: 30 | 60 | 90; at: string | null }[] = [
    { days: 30, at: d.retention_30_at },
    { days: 60, at: d.retention_60_at },
    { days: 90, at: d.retention_90_at },
  ];
  return (
    <Card>
      <CardHeader title={t("profile.retention.title")} subtitle={d.hired_job ? t("profile.retention.subtitle", { job: d.hired_job.title, carrier: d.hired_job.carrier_name, date: formatDate(d.start_date) }) : undefined} />
      <CardBody className="flex flex-wrap gap-2">
        {rows.map(({ days, at }) => {
          const due = d.start_date ? dayjs(d.start_date).add(days, "day") : null;
          const reachable = !due || !due.isAfter(dayjs(), "day");
          return at ? (
            <span key={days} className="inline-flex items-center gap-1 rounded-full bg-status-hired px-3 py-1 text-[12px] font-semibold">
              <Check size={12} /> {t("profile.retention.confirmedChip", { days, date: formatDate(at) })}
            </span>
          ) : (
            <Button key={days} size="sm" disabled={!reachable || mark.isPending} onClick={() => mark.mutate(days, { onSuccess: () => toast.success(t("profile.retention.confirmed", { days })), onError: (e) => toast.error(e instanceof ApiError ? e.message : tc("toast.failed")) })} title={!reachable && due ? t("profile.retention.dueTitle", { date: due.format("MMM D") }) : undefined}>
              {t("profile.retention.confirm", { days })}{!reachable && due ? ` · ${t("profile.retention.due", { date: due.format("MMM D") })}` : ""}
            </Button>
          );
        })}
      </CardBody>
    </Card>
  );
}

// ---- Arizalar ----

function ApplicationsTable({ apps, driverName, compact }: { apps: ApplicationBrief[]; driverName: string; compact?: boolean }) {
  const { t } = useTranslation("drivers");
  const { t: tm } = useTranslation("menu");
  const navigate = useNavigate();
  // Carrier to'lovi: holat hammaga ko'rinadi (jadvaldagidek), belgilash -- "budget" ruxsati bilan
  const canFee = can(useMe().data, "budget");
  const [feeFor, setFeeFor] = useState<FeeTarget | null>(null);
  const [payFor, setPayFor] = useState<FeeTarget | null>(null);
  const feeTarget = (a: ApplicationBrief): FeeTarget => ({
    id: a.id, version: a.version, driverName, carrierName: a.job.carrier_name, start_date: a.start_date,
    fee_status: a.fee_status, fee_amount: a.fee_amount, fee_due_date: a.fee_due_date, fee_paid_at: a.fee_paid_at, fee_note: a.fee_note,
    received: a.received, received_total: a.received_total, remaining: a.remaining, approved: a.approved,
  });
  // Belgi bosilsa: pul qabul qilsa bo'ladigan holatda -- to'lov oynasi, aks holda shartlar
  const openFee = (a: ApplicationBrief) => (canReceive(a.fee_status, a.approved) ? setPayFor(feeTarget(a)) : setFeeFor(feeTarget(a)));
  const appItems = (a: ApplicationBrief) => driverApplicationMenu(a, { navigate: (path) => navigate(path), openNewTab: openInNewTab, copy: (text) => void copyText(text) }, tm);
  if (apps.length === 0) return <div className="py-2 text-[13px] text-muted">{t("profile.apps.empty")}</div>;
  return (
    <Table variant="bare">
      <thead>
        <tr>
          <Th>{t("profile.apps.job")}</Th>
          <Th>{t("profile.apps.carrier")}</Th>
          <Th>{t("profile.apps.status")}</Th>
          <Th>{compact ? t("profile.apps.date") : t("profile.apps.submitted")}</Th>
          {!compact && <Th>{t("profile.apps.by")}</Th>}
          {!compact && <Th>{t("profile.apps.reply")}</Th>}
          {!compact && <Th>{t("profile.apps.decided")}</Th>}
          <Th>{t("profile.apps.payment")}</Th>
        </tr>
      </thead>
      <tbody>
        {apps.map((a) => (
          <ContextMenu key={a.id} items={() => appItems(a)}>
            <Tr>
              <Td>
                <Link to={`/jobs/${a.job.id}`} className="font-medium hover:underline">
                  {a.job.title}
                </Link>
                {a.pay_plan && <div className="text-[11px] text-muted">{a.pay_plan}</div>}
              </Td>
              <Td>
                <Link to={`/carriers/${a.job.carrier_id}`} className="hover:underline">
                  {a.job.carrier_name}
                </Link>
              </Td>
              <Td>
                <StatusPill status={a.status} label={a.status === "rejected" && a.rejection_reason ? t("profile.apps.rejected", { reason: a.rejection_reason }) : undefined} />
              </Td>
              <Td className="whitespace-nowrap">{formatDate(a.submitted_at)}</Td>
              {!compact && <Td>{a.submitted_by ? <PersonLink id={a.submitted_by.id} name={a.submitted_by.display_name} /> : "—"}</Td>}
              {!compact && <Td className="text-ink-soft">{a.carrier_reply || a.note || "—"}</Td>}
              {!compact && <Td className="whitespace-nowrap">{a.decided_at ? formatDate(a.decided_at) : a.start_date ? t("profile.apps.start", { date: formatDate(a.start_date) }) : "—"}</Td>}
              <Td title={a.fee_note || undefined}>
                <FeeBadge status={a.fee_status} due={a.fee_due_date} paidAt={a.fee_paid_at} approved={a.approved} onClick={canFee && a.fee_status ? () => openFee(a) : undefined} />
              </Td>
            </Tr>
          </ContextMenu>
        ))}
      </tbody>
      <PaymentModal target={payFor} onOpenChange={(o) => !o && setPayFor(null)} />
      <FeeEditModal target={feeFor} onOpenChange={(o) => !o && setFeeFor(null)} />
    </Table>
  );
}

// ---- Faollik: composer, eslatma, lenta ----

const ACTIVITY_TYPES = ["call", "note", "sms", "email", "status", "system"];

function activityParts(a: Activity, myTz: string, t: (k: string, o?: Record<string, unknown>) => string): { when: string; who: string; kind: string } {
  const who = a.created_by?.display_name ?? t("profile.activity.system");
  let kind = ACTIVITY_TYPES.includes(a.type) ? t(`profile.activity.types.${a.type}`) : a.type;
  if (a.type === "call") {
    const mins = a.duration_sec ? Math.max(1, Math.round(a.duration_sec / 60)) : null;
    const outcome = a.outcome ? ((CALL_OUTCOMES as readonly string[]).includes(a.outcome) ? t(`profile.activity.outcomes.${a.outcome}`) : a.outcome.replace(/_/g, " ")) : "";
    kind = `${mins ? t("profile.activity.callMins", { mins }) : t("profile.activity.types.call")}${outcome ? ` · ${outcome}` : ""}`;
  }
  if (a.type === "system" && a.body === "Created") kind = t("profile.activity.created");
  return { when: formatWhen(a.created_at, myTz), who, kind };
}

function ActivityPanel({
  d, myTz, composerType, setComposerType, focusKey, onShowAll,
}: {
  d: DriverDetail;
  myTz: string;
  composerType: "note" | "call" | "sms" | "email";
  setComposerType: (t: "note" | "call" | "sms" | "email") => void;
  focusKey: number;
  onShowAll: () => void;
}) {
  const { t } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  const activities = useDriverActivities(d.id);
  const log = useLogActivity(d.id);
  const [body, setBody] = useState("");
  const [minutes, setMinutes] = useState("");
  const [outcome, setOutcome] = useState("answered");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (focusKey > 0) textareaRef.current?.focus();
  }, [focusKey]);

  async function save() {
    if (composerType === "note" && !body.trim()) return;
    try {
      await log.mutateAsync({
        type: composerType,
        body: body.trim(),
        duration_sec: composerType === "call" && minutes ? Number(minutes) * 60 : null,
        outcome: composerType === "call" ? outcome : "",
      });
      setBody("");
      setMinutes("");
      toast.success(composerType === "call" ? t("profile.activity.callLogged") : tc("toast.saved"));
    } catch (err) {
      toast.error(err instanceof ApiError ? (err.fields?.body?.join(" ") ?? err.message) : tc("toast.failed"));
    }
  }

  const feed = activities.data?.results ?? [];
  const shown = feed.slice(0, 6);

  return (
    <Card>
      <CardHeader title={t("profile.activity.title")} />
      <CardBody className="space-y-4">
        <div className="space-y-2">
          <div className="flex gap-2">
            <Select value={composerType} onChange={(e) => setComposerType(e.target.value as typeof composerType)} className="w-28" aria-label={t("profile.activity.type")}>
              <option value="note">{t("profile.activity.types.note")}</option>
              <option value="call">{t("profile.activity.types.call")}</option>
              <option value="sms">{t("profile.activity.types.sms")}</option>
              <option value="email">{t("profile.activity.types.email")}</option>
            </Select>
            {composerType === "call" && (
              <>
                <NumberInput min={0} maxLength={4} value={minutes} onChange={(e) => setMinutes(e.target.value)} placeholder={t("profile.activity.min")} className="w-20" aria-label={t("profile.activity.duration")} />
                <Select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="w-36" aria-label={t("profile.activity.outcome")}>
                  {CALL_OUTCOMES.map((o) => (
                    <option key={o} value={o}>
                      {t(`profile.activity.outcomes.${o}`)}
                    </option>
                  ))}
                </Select>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Textarea
              ref={textareaRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={2}
              placeholder={composerType === "call" ? t("profile.activity.placeholderCall") : t("profile.activity.placeholderNote")}
              className="flex-1"
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") save();
              }}
            />
            <Button variant="primary" onClick={save} loading={log.isPending} disabled={composerType === "note" && !body.trim()} className="self-end">
              {tc("actions.save")}
            </Button>
          </div>
        </div>

        {activities.isPending ? (
          <div className="py-4 text-[12px] text-muted">{t("profile.activity.loading")}</div>
        ) : shown.length === 0 ? (
          <div className="py-2 text-[12px] text-muted">{t("profile.activity.empty")}</div>
        ) : (
          <ol>
            {shown.map((a, i) => (
              <ActivityItem key={a.id} a={a} myTz={myTz} last={i === shown.length - 1} />
            ))}
          </ol>
        )}
        {feed.length > shown.length && (
          <button type="button" onClick={onShowAll} className="text-[12px] text-primary hover:underline">
            {t("profile.activity.showAll", { n: d.activity_count })}
          </button>
        )}
      </CardBody>
    </Card>
  );
}

function ActivityItem({ a, myTz, last }: { a: Activity; myTz: string; last?: boolean }) {
  const { t } = useTranslation("drivers");
  const { t: tm } = useTranslation("menu");
  const items = () => compactMenu([{ key: "copy", label: tm("activityItem.copyText"), icon: Copy, onSelect: () => void copyText(a.body) }]);
  const { when, who, kind } = activityParts(a, myTz, t);
  // Lenta (oldingi reja): tur belgisi aylanada, orasida yupqa chiziq
  const Icon = a.type === "call" ? Phone : a.type === "status" ? ArrowRight : a.type === "email" ? Mail : a.type === "sms" ? MessageSquareText : a.type === "system" ? FileText : MessageSquare;
  const tone =
    a.type === "call" && a.outcome === "answered" ? "bg-success-soft text-success" : a.type === "status" ? "bg-primary-soft text-primary" : a.type === "system" ? "bg-fill text-faint" : "bg-fill text-ink-soft";
  // "Yaratilgan" tizim yozuvi: matn sarlavhani takrorlamasin
  const body = a.type === "system" && a.body === "Created" ? "" : a.body;
  return (
    <ContextMenu items={() => items()} disabled={!a.body}>
      <li className="flex gap-3.5 rounded-2xl data-[state=open]:bg-fill/60">
        <div className="flex flex-col items-center">
          <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-full", tone)} aria-hidden>
            <Icon size={14} strokeWidth={2} />
          </span>
          {!last && <span className="mt-1.5 w-px flex-1 bg-line" aria-hidden />}
        </div>
        <div className={cn("min-w-0 flex-1 pt-1", last ? "pb-1" : "pb-4")}>
          <div className="flex items-baseline gap-3">
            <span className="min-w-0 flex-1 text-[14px] font-medium">{kind}</span>
            <span className="shrink-0 text-[12px] whitespace-nowrap text-muted">{when}</span>
          </div>
          <div className="text-[12px] text-muted">{who}</div>
          {body && <div className="mt-1 text-[13px] whitespace-pre-line text-ink-soft">{body}</div>}
        </div>
      </li>
    </ContextMenu>
  );
}

function ActivityFeedFull({ driverId, myTz }: { driverId: number; myTz: string }) {
  const { t } = useTranslation("drivers");
  const [page, setPage] = useState(1);
  const activities = useDriverActivities(driverId, page);
  if (activities.isPending) return <PageLoading />;
  if (activities.isError) return <ErrorState message={activities.error.message} onRetry={() => activities.refetch()} />;
  const data = activities.data;
  return (
    <Card>
      <CardBody className="pt-4">
        {data.results.length === 0 ? (
          <EmptyState title={t("profile.activity.emptyTitle")} />
        ) : (
          <ol>
            {data.results.map((a, i) => (
              <ActivityItem key={a.id} a={a} myTz={myTz} last={i === data.results.length - 1} />
            ))}
          </ol>
        )}
        {data.pages > 1 && (
          <div className="mt-4 flex items-center justify-between text-[12px] text-muted">
            <span>
              {t("profile.activity.page", { page: data.page, pages: data.pages })}
            </span>
            <div className="flex gap-1">
              <Button size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                ‹
              </Button>
              <Button size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>
                ›
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function HistoryList({ driverId, myTz }: { driverId: number; myTz: string }) {
  const { t } = useTranslation("drivers");
  const history = useDriverHistory(driverId);
  if (history.isPending) return <PageLoading />;
  if (history.isError) return <ErrorState message={history.error.message} onRetry={() => history.refetch()} />;
  return (
    <Card>
      <CardBody className="pt-4">
        <Table variant="bare">
          <thead>
            <tr>
              <Th>{t("profile.history.when")}</Th>
              <Th>{t("profile.history.change")}</Th>
              <Th>{t("profile.history.reason")}</Th>
              <Th>{t("profile.history.job")}</Th>
              <Th>{t("profile.history.by")}</Th>
            </tr>
          </thead>
          <tbody>
            {history.data.map((h) => (
              <Tr key={h.id}>
                <Td className="whitespace-nowrap">{formatDateTime(h.changed_at, myTz)}</Td>
                <Td className="whitespace-nowrap">
                  {h.from_status ? <StatusPill status={h.from_status} /> : <span className="text-muted">—</span>} <span className="mx-1 text-muted">→</span> <StatusPill status={h.to_status} />
                </Td>
                <Td className="text-ink-soft">{[h.reason, h.note].filter(Boolean).join(" · ") || "—"}</Td>
                <Td>{h.job ? `${h.job.title} · ${h.job.carrier_name}` : "—"}</Td>
                <Td>{h.changed_by ? <PersonLink id={h.changed_by.id} name={h.changed_by.display_name} /> : t("profile.activity.system")}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </CardBody>
    </Card>
  );
}

// ---- Tahrirlash oynasi ----

function EditDriverModal({ d, open, onOpenChange, canEditSensitive, showOwner }: { d: DriverDetail; open: boolean; onOpenChange: (o: boolean) => void; canEditSensitive: boolean; showOwner: boolean }) {
  const { t } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  const driverSchema = useDriverSchema("edit");
  const qc = useQueryClient();
  const update = useUpdateDriver(d.id);
  const owners = useUserOptions({ role: "recruiter" }, open && showOwner);
  const form = useForm<DriverFormValues>({ resolver: zodResolver(driverSchema), defaultValues: fromDriverDetail(d) });

  useEffect(() => {
    if (open) form.reset(fromDriverDetail(d));
  }, [open, d, form]);

  async function onSubmit(values: DriverFormValues) {
    const ownerChanged = showOwner && values.owner !== (d.owner ? String(d.owner.id) : "");
    const payload = toDriverWrite(values, { includeOwner: ownerChanged, sensitive: canEditSensitive });
    try {
      await update.mutateAsync({ ...payload, version: d.version });
      toast.success(t("profile.edit.updated"));
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.isConflict) {
        toast.warning(t("statusModal.changedBy", { who: err.changedBy ?? t("statusModal.someoneElse") }), t("profile.edit.conflictDesc"));
        qc.invalidateQueries({ queryKey: driverKeys.detail(d.id) });
        onOpenChange(false);
        return;
      }
      if (err instanceof ApiError && err.fields) {
        for (const [k, msgs] of Object.entries(err.fields)) {
          if (k in values) form.setError(k as keyof DriverFormValues, { type: "server", message: msgs.join(" ") });
        }
        toast.error(t("profile.edit.checkFields"));
        return;
      }
      toast.error(err instanceof ApiError ? err.message : tc("toast.failed"));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("profile.edit.title", { name: d.full_name })}
      description={t("profile.edit.desc")}
      size="xl"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{tc("actions.cancel")}</Button>
          <Button variant="primary" loading={update.isPending} onClick={form.handleSubmit(onSubmit)}>
            {tc("actions.saveChanges")}
          </Button>
        </>
      }
    >
      <div className="max-h-[65vh] overflow-y-auto pr-1">
        <DriverFormFields form={form} mode="edit" sensitive={canEditSensitive} ownerOptions={showOwner ? (owners.data?.results ?? []) : undefined} />
      </div>
    </Modal>
  );
}

export type { Reminder };
